import React, { ReactNode } from 'react';
import classNames from 'classnames';
import { CellType } from './Table.types';

interface TableCellProps {
  /**
   * Cell value to display
   */
  value: any;
  
  /**
   * Type of cell content
   */
  type: CellType;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Column span
   */
  colSpan?: number;
}

/**
 * TableCell component that renders different types of cell content.
 */
const TableCell: React.FC<TableCellProps> = ({
  value,
  type = 'text',
  className,
  colSpan,
}) => {
  const renderContent = (): ReactNode => {
    switch (type) {
      case 'badge':
        return (
          <span 
            className={classNames(
              'px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full',
              {
                'bg-green-100 text-green-800': value === 'success' || value === 'active' || value === 'completed',
                'bg-yellow-100 text-yellow-800': value === 'warning' || value === 'pending' || value === 'processing',
                'bg-red-100 text-red-800': value === 'error' || value === 'failed',
                'bg-blue-100 text-blue-800': value === 'info' || value === 'new',
                'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-100': !value || value === 'none',
              }
            )}
          >
            {value}
          </span>
        );

      case 'image':
        return value ? (
          <img 
            src={value} 
            alt="Cell" 
            className="h-10 w-10 rounded-full object-cover"
          />
        ) : null;
        
      case 'component':
        return value ? value : null;
        
      case 'actions':
        return (
          <div className="flex items-center space-x-2 justify-end">
            {value}
          </div>
        );
        
      case 'text':
      default:
        if (value === null || value === undefined) return '-';
        // Handle objects carefully to avoid circular references
        if (value === null || value === undefined) return '-';
        if (typeof value === 'object') {
          try {
            // Only attempt to stringify simple objects
            if (!React.isValidElement(value)) {
              return JSON.stringify(value);
            }
            // If it's a React element, just return it directly
            return value;
          } catch (error) {
            // Fallback if stringification fails
            return '[Complex Object]';
          }
        }
        return value;
    }
  };
  
  return (
    <td 
      className={classNames('px-4 py-3 border-r border-gray-200/80 dark:border-white/10 last:border-r-0 text-gray-700 dark:text-gray-200', className)} 
      colSpan={colSpan}
    >
      {renderContent()}
    </td>
  );
};

export default TableCell;